import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

interface SiteNavProps {
  currentPage: 'home' | 'consulting' | 'terms';
  // Only provided on the home page; used so the same nav can scroll-to-anchor
  // when already on home, and route+scroll otherwise.
  onScrollTo?: (id: string) => void;
}

// Desktop link base styling — refined to match the Landing Hero design.
// Letter spacing removed per designer review (the previous tracking-[0.06em]
// made the nav read AI-stamped).
const desktopLinkClasses =
  'relative text-sm font-semibold text-gsp-text-primary uppercase transition-colors';
const mobileLinkClasses =
  'block w-full text-left px-4 py-3 rounded-lg font-semibold text-gsp-text-primary hover:bg-gray-50 uppercase tracking-wide';

// Hover-state orange underline that animates in from the left. Shared by all
// non-active desktop links so the nav feels consistent.
const HoverUnderline = () => (
  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gsp-orange rounded-full origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
);

// Solid orange underline used for the active page.
const ActiveUnderline = () => (
  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gsp-orange rounded-full" />
);

export function SiteNav({ currentPage, onScrollTo }: SiteNavProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isHome = currentPage === 'home';
  const loginHref = import.meta.env.VITE_APP_URL || 'https://app.propvetter.com';

  const closeMobile = () => setMobileMenuOpen(false);

  const renderHome = (mobile: boolean) => {
    if (mobile) {
      return isHome ? (
        <button
          onClick={() => { onScrollTo?.('home'); closeMobile(); }}
          className={`${mobileLinkClasses} text-gsp-teal`}
        >
          Home
        </button>
      ) : (
        <Link to="/" onClick={closeMobile} className={mobileLinkClasses}>
          Home
        </Link>
      );
    }
    // Desktop: active page shows the solid orange underline; other pages get
    // the animated hover underline.
    return isHome ? (
      <button
        onClick={() => onScrollTo?.('home')}
        className={`${desktopLinkClasses} group`}
      >
        Home
        <ActiveUnderline />
      </button>
    ) : (
      <Link to="/" className={`${desktopLinkClasses} group`}>
        Home
        <HoverUnderline />
      </Link>
    );
  };

  const renderConsulting = (mobile: boolean) => {
    const isActive = currentPage === 'consulting';
    if (mobile) {
      return (
        <Link
          to="/consulting"
          onClick={closeMobile}
          className={`${mobileLinkClasses}${isActive ? ' text-gsp-teal' : ''}`}
        >
          Advisory
        </Link>
      );
    }
    return (
      <Link to="/consulting" className={`${desktopLinkClasses} group`}>
        Advisory
        {isActive ? <ActiveUnderline /> : <HoverUnderline />}
      </Link>
    );
  };

  const renderContact = (mobile: boolean) => {
    if (mobile) {
      return isHome ? (
        <button
          onClick={() => { onScrollTo?.('contact'); closeMobile(); }}
          className={mobileLinkClasses}
        >
          Contact
        </button>
      ) : (
        <Link to="/#contact" onClick={closeMobile} className={mobileLinkClasses}>
          Contact
        </Link>
      );
    }
    return isHome ? (
      <button
        onClick={() => onScrollTo?.('contact')}
        className={`${desktopLinkClasses} group`}
      >
        Contact
        <HoverUnderline />
      </button>
    ) : (
      <Link to="/#contact" className={`${desktopLinkClasses} group`}>
        Contact
        <HoverUnderline />
      </Link>
    );
  };

  return (
    <nav className="w-full z-50 sticky top-0 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <img
            src="/propvetter_logo_medium.png"
            alt="Propvetter by George Smith Partners"
            className="h-10 sm:h-12 w-auto"
          />
        </Link>

        <div className="hidden md:flex items-center gap-7">
          {renderHome(false)}
          {renderConsulting(false)}
          {renderContact(false)}
          <a
            href="https://www.gspartners.com/about/"
            target="_blank"
            rel="noopener noreferrer"
            className={`${desktopLinkClasses} group`}
          >
            About GSP
            <HoverUnderline />
          </a>
          {/* Vertical divider sets the Login CTA visually apart from the
              text links — matches the refined design. */}
          <span className="h-5 w-px bg-gray-200" />
          <a
            href={loginHref}
            className="inline-flex items-center justify-center bg-gsp-orange text-white px-5 py-2.5 rounded-[10px] font-semibold text-sm hover:bg-gsp-orange/90 transition-colors shadow-sm"
          >
            Login
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 text-gsp-navy hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 animate-in slide-in-from-top duration-200">
          <div className="px-4 py-3 space-y-2">
            {renderHome(true)}
            {renderConsulting(true)}
            {renderContact(true)}
            <a
              href="https://www.gspartners.com/about/"
              target="_blank"
              rel="noopener noreferrer"
              className={mobileLinkClasses}
            >
              About GSP
            </a>
            <a
              href={loginHref}
              className="flex items-center justify-center bg-gsp-orange text-white px-4 py-3 rounded-[10px] font-semibold hover:bg-gsp-orange/90 transition-colors"
            >
              Login
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

export default SiteNav;
